import { Component, Inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MenuStoreService } from '../../../services/menu/menu-store.service';
import { forkJoin, of } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { IChainMenu, MenuFile } from '../../../interfaces/menu';
import { ConfirmationDialogComponent } from '../confirm-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { IRestaurantInfo } from '../../../interfaces/restaurant';
import { arraysEqual } from '../../../shared/function';

type MenuLanguage = string | null;
type ExistingFileValue = File | string | null;

type ChainMenuItemForm = {
  id: FormControl<number | null>;
  name: FormControl<string>;
  language: FormControl<MenuLanguage>;
  hidden: FormControl<boolean>;
  menuFile: FormControl<File | MenuFile | null>;
  iconFile: FormControl<ExistingFileValue>;
  bannerFile: FormControl<ExistingFileValue>;
};

type EditChainMenuForm = {
  happyHours: FormControl<boolean>;
  restaurants: FormControl<number[] | null>;
  toAll: FormControl<boolean>;
  menusFormArray: FormArray<FormGroup<ChainMenuItemForm>>;
};

@UntilDestroy()
@Component({
  selector: 'app-edit-chain-menu',
  templateUrl: './edit-chain-menu.component.html',
  styleUrls: ['./edit-chain-menu.component.scss'],
})
export class EditChainMenuComponent {
  public selected = 0;
  public languages = [
    { value: 'en-CA', viewValue: 'English' },
    { value: 'fr-CA', viewValue: 'French' },
    { value: null, viewValue: 'Not Set' },
  ];
  public menus = this.data.menusGroup;
  public isMainMenu = this.data.isMainMenu;
  public happyHours = this.data.menusGroup?.[0].happyHours ?? false;
  public form: FormGroup<EditChainMenuForm> = this.createMenuFormGroup();
  public isUpdating = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      chainId: number;
      isMainMenu: boolean;
      originalId: number;
      menusGroup: IChainMenu[];
      restaurants: IRestaurantInfo[];
    },
    private menuState: MenuStoreService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<EditChainMenuComponent>,
  ) {
    this.form.controls.toAll.valueChanges.pipe(
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe((value: boolean) => {
      if (value) {
        this.form.controls.restaurants.reset({
          value: [],
          disabled: true,
        });
      } else {
        this.form.controls.restaurants.reset({
          value: this.data.menusGroup[0]?.attachedRestaurantIds,
          disabled: false,
        });
      }
    });
  }

  private createMenuFormGroup(): FormGroup<EditChainMenuForm> {
    return new FormGroup<EditChainMenuForm>({
      happyHours: new FormControl(this.data.menusGroup[0].happyHours, { nonNullable: true }),
      restaurants: new FormControl<number[] | null>({
        value: this.data.menusGroup[0].attachedToAll ? null : this.data.menusGroup[0]?.attachedRestaurantIds,
        disabled: this.data.menusGroup[0].attachedToAll
      }),
      toAll: new FormControl(this.data.menusGroup[0].attachedToAll, { nonNullable: true }),
      menusFormArray: new FormArray<FormGroup<ChainMenuItemForm>>(
        this.data.menusGroup.map(menu => this.generateMenuFormArray(menu))
      )
    });
  }

  private generateMenuFormArray(menu: IChainMenu): FormGroup<ChainMenuItemForm> {
    return new FormGroup<ChainMenuItemForm>({
      id: new FormControl(menu.id, { nonNullable: true }),
      name: new FormControl(menu.name, { nonNullable: true, validators: [Validators.required, Validators.minLength(3)] }),
      language: new FormControl<MenuLanguage>(menu.language),
      hidden: new FormControl(menu.hidden, { nonNullable: true }),
      menuFile: new FormControl<File | MenuFile | null>(menu.file || null, [Validators.required]),
      iconFile: new FormControl<ExistingFileValue>(menu.icon || null),
      bannerFile: new FormControl<ExistingFileValue>(menu.banner || null)
    });
  }

  get formArray(): FormArray<FormGroup<ChainMenuItemForm>> {
    return this.form.controls.menusFormArray;
  }

  get menusControls(): Array<FormGroup<ChainMenuItemForm>> {
    return this.formArray.controls;
  }

  public menuPreviewUrl(index: number): string {
      return this.menus[index]?.pages?.[0] ?? '';
  }

  public iconPreviewUrl(index: number): string {
    return this.menus[index]?.icon ?? '';
  }

  get selectedLanguage(): MenuLanguage {
    return this.menusControls[this.selected].controls.language.value;
  }

  public isLanguageAvailable(language: MenuLanguage, currentIndex: number): boolean {
    const selectedLanguages = this.menusControls
      .map((menu, index) => index !== currentIndex ? menu.controls.language.value : null)
      .filter(lang => lang !== null);

    return !selectedLanguages.includes(language) && !(this.menusControls.length > 1 && language === null);
  }

  public addTranslate(): void {
    this.formArray.push(this.createNewTranslationFormGroup());

    this.selected = this.formArray.length - 1;
  }

  public removeTranslate(index: number, id: number | null): void {
    if (id) {
      const title = this.translate.instant('pages.menu.modal.delete');
      const message = this.translate.instant('pages.menu.messages.confirmDelete');
      const confirmationModal = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message,
          title
        },
        panelClass: 'confirmation-modal'
      });

      confirmationModal.afterClosed().pipe(
        filter(Boolean),
        untilDestroyed(this)
      ).subscribe((): void => {
        this.delete(this.data.chainId, id, index);
      });
    } else {
      this.formArray.removeAt(index);
      this.selected = index;
    }
  }

  update(): void {
    this.isUpdating = true;

    const [menuOriginalData, ...menuTranslatedData] = this.menusControls;
    const [originalMenu, ...translatedMenus] = this.data.menusGroup;
    const originalMenuId = originalMenu.id;
    const formValue = this.form.getRawValue();

    let updatedRestaurants: number[] | null;

    if (formValue.toAll) {
      updatedRestaurants = [];
    } else if (
      (!formValue.toAll && formValue.restaurants?.length === 0) ||
      this.isNullInArray(formValue.restaurants)
    ) {
      updatedRestaurants = null;
    } else {
      updatedRestaurants = formValue.restaurants;
    }

    const updateChainMenu = (menuControl: FormGroup<ChainMenuItemForm>) => {
      const menuData = menuControl.getRawValue();

      if (menuData.id === null) {
        throw new Error('Menu id is required');
      }

      return this.menuState.updateChainMenu(
        menuData.id,
        this.data.chainId,
        updatedRestaurants,
        {
          name: menuData.name,
          language: menuData.language,
          happyHours: formValue.happyHours,
          hidden: menuData.hidden,
          menuFile: menuData.menuFile,
          iconFile: menuData.iconFile,
          bannerFile: menuData.bannerFile
        }
      );
    };

    const addChainMenu = (menuControl: FormGroup<ChainMenuItemForm>, originalId: number | null) => {
      const menuData = menuControl.getRawValue();

      if (!(menuData.menuFile instanceof File)) {
        throw new Error('Menu file is required');
      }

      return this.menuState.addChainMenu(
        this.data.chainId,
        menuData.name,
        menuData.language,
        formValue.happyHours,
        menuData.hidden,
        menuData.menuFile,
        updatedRestaurants,
        originalId,
        menuData.iconFile instanceof File ? menuData.iconFile : undefined,
        menuData.bannerFile instanceof File ? menuData.bannerFile : undefined
      );
    };

    const hasOriginalChanged = this.isChangedOriginal();
    const changedTranslations: Array<FormGroup<ChainMenuItemForm>> = menuTranslatedData
      .map((menuControl, index) => {
        const menu = translatedMenus[index];
        return this.isMenuChanged(menuControl, menu) ? menuControl : null;
      })
      .filter((ctrl): ctrl is FormGroup<ChainMenuItemForm> => ctrl !== null);

    const onComplete = () => {
      this.dialogRef?.close?.();
      this.isUpdating = false;
    };

    const onError = () => {
      this.isUpdating = false;
    };

    if (!hasOriginalChanged && changedTranslations.length === 0) {
      this.isUpdating = false;
      return;
    }

    const handleTranslations = (originalId: number) => {

      if (changedTranslations.length === 0) {
        return of(null);
      }

      return forkJoin(changedTranslations.map(menuControl => {
        return menuControl.controls.id.value
          ? updateChainMenu(menuControl)
          : addChainMenu(menuControl, originalId);
      }));
    };

    const observable$ = hasOriginalChanged
      ? updateChainMenu(menuOriginalData).pipe(
        switchMap(updatedOriginal => handleTranslations(updatedOriginal.id))
      )
      : handleTranslations(originalMenuId);

    observable$.pipe(
      untilDestroyed(this)
    ).subscribe({
      next: () => onComplete(),
      error: () => onError()
    });
  }

  public isNullInArray(restaurants: number[] | null): boolean {
    return restaurants?.length === 1 && restaurants[0] === null;
  }

  delete(chainId: number, menuId: number, index: number): void {
    this.isUpdating = true;
    this.menuState
      .removeChainMenu(chainId, menuId)
      .pipe(untilDestroyed(this))
      .subscribe(
        () => {
          this.formArray.removeAt(index);

          if (this.formArray.length === 0 || this.data.originalId === menuId) {
            this.dialog.closeAll();
          } else {
            this.selected = index;
            this.isUpdating = false;
          }
        },
        () => {
          this.isUpdating = false;
          this.dialog.closeAll();
        }
      );
  }

  public isChangedOriginal(): boolean {
    const menuOriginalData = this.menusControls[0];
    const menu = this.data.menusGroup[0];
    const formValue = this.form.getRawValue();
    const menuValue = menuOriginalData.getRawValue();

    const isEqual = (
      menuValue.name !== menu.name ||
      menuValue.language !== menu.language ||
      menuValue.hidden !== menu.hidden ||
      menuValue.menuFile !== menu.file ||
      menuValue.iconFile !== menu.icon ||
      menuValue.bannerFile !== menu.banner ||
      formValue.happyHours !== menu.happyHours ||
      formValue.toAll !== menu.attachedToAll ||
      (formValue.toAll
        ? false
        : !arraysEqual(formValue.restaurants, menu.attachedRestaurantIds))
    );

    return isEqual;
  }

  public isChangedTranslations(): boolean {
    const menuTranslationData = this.menusControls.slice(1);
    const translation = this.data.menusGroup.slice(1);

    if (menuTranslationData.length !== translation.length) {
      return true;
    }

    return menuTranslationData.some((menuControl, index) =>
      this.isMenuChanged(menuControl, translation[index])
    );
  }

  private createNewTranslationFormGroup(): FormGroup<ChainMenuItemForm> {
    return new FormGroup<ChainMenuItemForm>({
      id: new FormControl<number | null>(null),
      name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(3)] }),
      language: new FormControl<MenuLanguage>(null, [Validators.required]),
      hidden: new FormControl(false, { nonNullable: true }),
      menuFile: new FormControl<File | MenuFile | null>(null, [Validators.required]),
      iconFile: new FormControl<ExistingFileValue>(null),
      bannerFile: new FormControl<ExistingFileValue>(null)
    });
  }

  private isMenuChanged(menuControl: FormGroup<ChainMenuItemForm>, menuData?: IChainMenu): boolean {
    const menuValue = menuControl.getRawValue();
    const isNew = !menuValue.id;

    return isNew || (
      menuValue.name !== menuData?.name ||
      menuValue.language !== menuData?.language ||
      menuValue.hidden !== menuData?.hidden ||
      menuValue.menuFile !== menuData?.file ||
      menuValue.iconFile !== menuData?.icon ||
      menuValue.bannerFile !== menuData?.banner ||
      this.form.controls.happyHours.value !== menuData?.happyHours
    );
  }
}
